/**
 * Quality gate dal lato della pagina: avanzamento, freeze, campione, sigillo.
 *
 * Le scritture passano da qui e da nessun altro posto. Offline non si congela,
 * non si campiona, non si ricontrolla, non si sigilla e non si scongela: il
 * servizio e' l'autorita', e una mutazione in coda locale sul quality gate
 * sarebbe una fotografia che il servente non ha mai visto.
 */
import { drop, get, newKey, send } from './api'

export interface QualityProgress {
  inspection_id: string
  review_epoch: number
  frozen: boolean
  freeze_id: string | null
  frozen_review_version: number | null
  supervisor_review_version: number | null
  waiting: number
  decided: number
  manual: number
  coverage_gaps: number
  coverage_declared: number
  frames_reviewed: number
  active_sessions: number
  active_leases: number
  pending_intents: number
  conflicts: number
  invalid_findings: number
  by_level: Record<string, number>
  by_code: Record<string, number>
  by_origin: Record<string, number>
  by_operator: Record<string, number>
  sample_id: string | null
  sample_required: number | null
  sample_reviewed: number | null
  seal_id: string | null
  seal_valid: boolean
}

export interface ReviewFreeze {
  id: string
  inspection_id: string
  review_epoch: number
  frozen: boolean
  frozen_review_version: number
  supervisor_review_version: number
  frozen_at: string
  frozen_by_id: string
  thawed_at: string | null
}

export interface QualitySample {
  id: string
  inspection_id: string
  freeze_id: string
  seed: string
  canonical_query: string
  algorithm_version: string
  population_size: number
  required_size: number
  selected_ids: string[]
  order: string[]
  strata: Record<string, unknown>
  reviews: { detection_id: string; decision: 'confirm' | 'overturn' }[]
  /** Che cosa e' ogni caso: senza, si giudica un identificativo. */
  cases: {
    detection_id: string
    code: string
    level: number | null
    external_element_id: number | null
  }[]
}

export interface SecondaryReview {
  id: string
  inspection_id: string
  sample_id: string
  detection_id: string
  decision: 'confirm' | 'overturn'
  supervisor_review_version: number
  created_at: string
}

export interface QualitySeal {
  id: string
  inspection_id: string
  freeze_id: string
  sample_id: string
  review_epoch: number
  frozen_review_version: number
  supervisor_review_version: number
  sealed_at: string
  valid: boolean
  invalidated_at: string | null
  invalidation_reason: string | null
}

export function readProgress(inspectionId: string): Promise<QualityProgress> {
  return get<QualityProgress>(`/inspections/${inspectionId}/quality-progress`)
}

export function freezeReview(inspectionId: string): Promise<ReviewFreeze> {
  return send<ReviewFreeze>(`/inspections/${inspectionId}/review-freeze`, {}, newKey())
}

export function unfreezeReview(inspectionId: string): Promise<ReviewFreeze> {
  return drop(`/inspections/${inspectionId}/review-freeze`) as unknown as Promise<ReviewFreeze>
}

export function createSample(inspectionId: string, seed?: string): Promise<QualitySample> {
  return send<QualitySample>(
    `/inspections/${inspectionId}/quality-samples`,
    seed ? { seed } : {},
    newKey(),
  )
}

export function readSample(inspectionId: string, sampleId: string): Promise<QualitySample> {
  return get<QualitySample>(`/inspections/${inspectionId}/quality-samples/${sampleId}`)
}

export function addSecondary(
  inspectionId: string,
  body: { sample_id: string; detection_id: string; decision: 'confirm' | 'overturn'; note?: string },
): Promise<SecondaryReview> {
  return send<SecondaryReview>(`/inspections/${inspectionId}/secondary-reviews`, body, newKey())
}

export function applySeal(inspectionId: string, sampleId: string): Promise<QualitySeal> {
  return send<QualitySeal>(`/inspections/${inspectionId}/quality-seal`, { sample_id: sampleId }, newKey())
}

export function readSeal(inspectionId: string): Promise<QualitySeal> {
  return get<QualitySeal>(`/inspections/${inspectionId}/quality-seal`)
}
